import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Zap,
  Target,
  Trophy,
  Brain,
  Layers,
  ArrowRight,
  Check,
  Star,
  Users,
  Sparkles,
  Play,
  Monitor,
  Server,
  Database,
  Cloud,
} from 'lucide-react';
import { subscriptionAPI } from '../services/api';

const LandingPage = () => {
  const [premiumPrice, setPremiumPrice] = useState(499); // Default fallback

  useEffect(() => {
    // Fetch actual price from backend
    subscriptionAPI.getPlans()
      .then(res => {
        const premiumPlan = res.data.plans?.find(p => p.id === 'premium_annual');
        if (premiumPlan?.price) {
          setPremiumPrice(Math.round(premiumPlan.price / 100)); // Convert paise to INR
        }
      })
      .catch(err => {
        console.log('Could not fetch pricing, using default:', err);
      });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="relative">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <span className="text-white font-bold text-lg">B</span>
                </div>
                <Sparkles className="absolute -top-1 -right-1 w-4 h-4 text-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <span className="text-xl font-bold text-white">buildex.dev</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link
                to="/challenges"
                className="text-slate-300 hover:text-white transition-colors font-medium"
              >
                Challenges
              </Link>
              <Link
                to="/login"
                className="text-slate-300 hover:text-white transition-colors font-medium"
              >
                Login
              </Link>
              <Link
                to="/register"
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full mb-8">
            <Sparkles className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-blue-300">Learn System Design the Fun Way</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Master System Design
            <br />
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 text-transparent bg-clip-text">
              Through Practice
            </span>
          </h1>

          <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-10">
            Build real architectures, get AI-powered feedback, and level up your skills.
            The gamified platform that makes system design interview prep actually enjoyable.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Link
              to="/register"
              className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold text-lg hover:shadow-xl hover:shadow-purple-500/25 hover:scale-105 transition-all"
            >
              Start Building Free
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/try"
              className="flex items-center gap-2 px-8 py-4 bg-slate-800 text-white rounded-xl font-semibold text-lg border border-slate-700 hover:border-slate-600 hover:bg-slate-700 transition-all"
            >
              <Play className="w-5 h-5" />
              Try Free Challenge
            </Link>
          </div>

          {/* Architecture Preview */}
          <div className="relative max-w-4xl mx-auto">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl blur-3xl"></div>
            <div className="relative bg-slate-800/50 border border-slate-700 rounded-2xl p-8 backdrop-blur-sm">
              <div className="flex items-center justify-center gap-8 flex-wrap">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-16 h-16 bg-pink-500/20 rounded-xl flex items-center justify-center border border-pink-500/30">
                    <Users className="w-8 h-8 text-pink-400" />
                  </div>
                  <span className="text-sm text-slate-400">Users</span>
                </div>
                <ArrowRight className="w-6 h-6 text-slate-600" />
                <div className="flex flex-col items-center gap-2">
                  <div className="w-16 h-16 bg-cyan-500/20 rounded-xl flex items-center justify-center border border-cyan-500/30">
                    <Cloud className="w-8 h-8 text-cyan-400" />
                  </div>
                  <span className="text-sm text-slate-400">Load Balancer</span>
                </div>
                <ArrowRight className="w-6 h-6 text-slate-600" />
                <div className="flex flex-col items-center gap-2">
                  <div className="w-16 h-16 bg-blue-500/20 rounded-xl flex items-center justify-center border border-blue-500/30">
                    <Server className="w-8 h-8 text-blue-400" />
                  </div>
                  <span className="text-sm text-slate-400">Servers</span>
                </div>
                <ArrowRight className="w-6 h-6 text-slate-600" />
                <div className="flex flex-col items-center gap-2">
                  <div className="w-16 h-16 bg-green-500/20 rounded-xl flex items-center justify-center border border-green-500/30">
                    <Database className="w-8 h-8 text-green-400" />
                  </div>
                  <span className="text-sm text-slate-400">Database</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-slate-800/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why Engineers Love Buildex
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Stop reading, start building. Our interactive platform turns theory into practice.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 hover:border-blue-500/50 transition-colors">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4">
                <Layers className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Drag & Drop Builder</h3>
              <p className="text-slate-400">
                Build architectures visually with our intuitive canvas. Connect components, configure settings, and see your design come to life.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 hover:border-purple-500/50 transition-colors">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4">
                <Brain className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">AI-Powered Feedback</h3>
              <p className="text-slate-400">
                Get instant, detailed feedback on your designs. Our AI evaluates scalability, reliability, cost, and more.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 hover:border-green-500/50 transition-colors">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Real Interview Questions</h3>
              <p className="text-slate-400">
                Practice with challenges inspired by actual FAANG interview questions. Design Twitter, Uber, Netflix, and more.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 hover:border-yellow-500/50 transition-colors">
              <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center mb-4">
                <Trophy className="w-6 h-6 text-yellow-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Gamified Learning</h3>
              <p className="text-slate-400">
                Earn XP, unlock components, collect achievements, and climb the leaderboard. Learning has never been this fun.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 hover:border-pink-500/50 transition-colors">
              <div className="w-12 h-12 bg-pink-500/20 rounded-xl flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-pink-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Component Library</h3>
              <p className="text-slate-400">
                Access 100+ real-world components: databases, caches, queues, load balancers, and cloud services from AWS, GCP, Azure.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 hover:border-cyan-500/50 transition-colors">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center mb-4">
                <Monitor className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Reference Solutions</h3>
              <p className="text-slate-400">
                Stuck? View expert-designed reference architectures. Learn best practices from optimal solutions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-lg text-slate-400">
              Three simple steps to system design mastery
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-white">
                1
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Pick a Challenge</h3>
              <p className="text-slate-400">
                Choose from challenges like "Design Twitter" or "Build a URL Shortener". Each has clear requirements and constraints.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-white">
                2
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Build Your Design</h3>
              <p className="text-slate-400">
                Drag components onto the canvas, connect them, and configure your architecture to meet the requirements.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-white">
                3
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Get AI Feedback</h3>
              <p className="text-slate-400">
                Submit and receive instant, detailed feedback. Learn what you did well and how to improve.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-20 px-4 bg-slate-800/30">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Simple Pricing
            </h2>
            <p className="text-lg text-slate-400">
              Start free, upgrade when you're ready
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-8">
            {/* Free Plan */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-white mb-2">Free</h3>
              <p className="text-slate-400 mb-6">Perfect for getting started</p>
              <div className="mb-6">
                <div className="text-4xl font-bold text-white">
                  ₹0 <span className="text-lg text-slate-400 font-normal">/forever</span>
                </div>
                <div className="text-sm text-slate-400 mt-1">No credit card required</div>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-slate-300">
                  <Check className="w-5 h-5 text-green-400" />
                  2 AI evaluations per day
                </li>
                <li className="flex items-center gap-3 text-slate-300">
                  <Check className="w-5 h-5 text-green-400" />
                  Starter challenges
                </li>
                <li className="flex items-center gap-3 text-slate-300">
                  <Check className="w-5 h-5 text-green-400" />
                  Basic component library
                </li>
              </ul>
              <Link
                to="/register"
                className="block w-full py-3 text-center bg-slate-700 text-white rounded-xl font-semibold hover:bg-slate-600 transition-colors"
              >
                Get Started Free
              </Link>
            </div>

            {/* Premium Plan */}
            <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-2xl p-8 relative">
              <div className="absolute -top-3 right-6">
                <div className="flex items-center gap-1 px-3 py-1 bg-yellow-400 text-yellow-900 rounded-full text-sm font-semibold">
                  <Star className="w-4 h-4" />
                  Popular
                </div>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Premium</h3>
              <p className="text-slate-400 mb-6">For serious learners</p>
              <div className="mb-6">
                <div className="text-4xl font-bold text-white">
                  ₹{premiumPrice} <span className="text-lg text-slate-400 font-normal">/year</span>
                </div>
                <div className="text-sm text-slate-400 mt-1">International cards accepted</div>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-slate-300">
                  <Check className="w-5 h-5 text-green-400" />
                  20 AI evaluations per day
                </li>
                <li className="flex items-center gap-3 text-slate-300">
                  <Check className="w-5 h-5 text-green-400" />
                  All challenges unlocked
                </li>
                <li className="flex items-center gap-3 text-slate-300">
                  <Check className="w-5 h-5 text-green-400" />
                  Full component library
                </li>
              </ul>
              <Link
                to="/register"
                className="block w-full py-3 text-center bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Ace Your System Design Interview?
          </h2>
          <p className="text-xl text-slate-400 mb-10">
            Join thousands of engineers practicing system design the smart way.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold text-lg hover:shadow-xl hover:shadow-purple-500/25 hover:scale-105 transition-all"
          >
            Create Free Account
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2 group">
              <div className="relative">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <span className="text-white font-bold text-lg">B</span>
                </div>
                <Sparkles className="absolute -top-1 -right-1 w-4 h-4 text-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <span className="text-xl font-bold text-white">buildex.dev</span>
            </div>
            <div className="flex items-center gap-6 text-slate-400">
              <Link to="/policy/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/policy/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/policy/refunds" className="hover:text-white transition-colors">Refunds</Link>
              <Link to="/policy/shipping" className="hover:text-white transition-colors">Shipping</Link>
              <Link to="/policy/contact" className="hover:text-white transition-colors">Contact</Link>
            </div>
            <p className="text-slate-500 text-sm">
              © 2025 buildex.dev. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
